import { Capacitor } from '@capacitor/core';
import { unifiedHttpsCallable } from '@watchlist/shared';

interface GenerateStoryImageResponse {
  url?: string;
  cached?: boolean;
  // Legacy fields returned by old CF before Storage upload was added:
  imageBase64?: string;
  mimeType?: string;
}

/**
 * Calls the Cloud Function to generate a story share card image server-side.
 * Returns a Storage URL (new CF) or falls back to a data URL (old CF) so the
 * client works before the CF is redeployed.
 */
export async function generateStoryShareImage(storyId: string, options?: { force?: boolean }): Promise<{ url: string; cached: boolean }> {
  const fn = unifiedHttpsCallable<{ type: 'story'; storyId: string; force?: boolean }, GenerateStoryImageResponse>(
    'generateShareImage',
    { timeout: 120_000, region: 'us-central1' }
  );
  const result = await fn({ type: 'story', storyId, force: options?.force });
  if (result.data?.url) {
    return { url: result.data.url, cached: result.data.cached ?? false };
  }
  // Backward compat: old CF returned base64 — wrap in a data URL
  if (result.data?.imageBase64) {
    const mime = result.data.mimeType ?? 'image/jpeg';
    return { url: `data:${mime};base64,${result.data.imageBase64}`, cached: false };
  }
  throw new Error('generateStoryShareImage: invalid response');
}

/**
 * Shares a pre-generated story image via the platform share sheet.
 * Pair with generateStoryShareImage() to pre-generate in the background.
 *
 * Priority:
 *   1. Capacitor native share (iOS / Android)
 *   2. Web Share API Level 2 (mobile Safari / Chromium)
 *   3. JPEG download fallback (desktop)
 */
export async function shareStoryImage(imageBase64: string, mimeType: string): Promise<void> {
  const filename = `watchlists-story-${Date.now()}.jpg`;
  const blob = base64ToBlob(imageBase64, mimeType);

  if (Capacitor.isNativePlatform()) {
    await shareNative(imageBase64, filename);
  } else if (canWebShare(blob, filename)) {
    await shareWeb(blob, filename);
  } else {
    downloadBlob(blob, filename);
  }
}

/**
 * Shares a story/watchlist share image from a Firebase Storage URL.
 * Fetches the image, then shares via native/web share sheet or downloads as fallback.
 */
export async function shareStoryImageFromUrl(url: string): Promise<void> {
  const filename = `watchlists-story-${Date.now()}.jpg`;
  // Handle legacy data URLs (backward compat before CF redeployment)
  if (url.startsWith('data:')) {
    const [header, base64] = url.split(',');
    const mimeType = header.replace('data:', '').replace(';base64', '');
    await shareStoryImage(base64, mimeType);
    return;
  }
  const response = await fetch(url);
  const blob = await response.blob();
  if (Capacitor.isNativePlatform()) {
    const base64 = await blobToBase64String(blob);
    await shareNative(base64, filename);
  } else if (canWebShare(blob, filename)) {
    await shareWeb(blob, filename);
  } else {
    downloadBlob(blob, filename);
  }
}

/**
 * Generates a story share card image server-side (Puppeteer Cloud Function)
 * and shares it via the platform share sheet.
 *
 * Server-side generation means TMDB images are fetched without any CORS
 * restrictions — the resulting JPEG always contains the full backdrop,
 * poster, review text, and author info regardless of platform.
 */
export async function captureAndShareStory(storyId: string): Promise<void> {
  const { url } = await generateStoryShareImage(storyId);
  await shareStoryImageFromUrl(url);
}

function base64ToBlob(base64: string, mimeType: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeType });
}

async function blobToBase64String(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function canWebShare(blob: Blob, filename: string): boolean {
  if (typeof navigator === 'undefined' || !navigator.share) return false;
  try {
    const file = new File([blob], filename, { type: blob.type });
    return navigator.canShare?.({ files: [file] }) ?? false;
  } catch {
    return false;
  }
}

async function shareNative(base64: string, filename: string): Promise<void> {
  const { Filesystem, Directory } = await import('@capacitor/filesystem');
  const { Share } = await import('@capacitor/share');

  const path = `story_share/${filename}`;
  await Filesystem.writeFile({ path, data: base64, directory: Directory.Cache, recursive: true });
  const { uri } = await Filesystem.getUri({ path, directory: Directory.Cache });
  await Share.share({ title: 'My review on Watchlists', files: [uri], dialogTitle: 'Share spotlight' });
}

async function shareWeb(blob: Blob, filename: string): Promise<void> {
  const file = new File([blob], filename, { type: blob.type });
  await navigator.share({ files: [file], title: 'My review on Watchlists' });
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
